import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Box, Button, Group, Text, Stack, Modal, Textarea, Badge } from '@mantine/core'
import { FiTrash2, FiAlertCircle, FiFlag } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { useStore } from '../../../app/stores/store'
import type { Firearm } from '../../../app/models/firearm'

interface AdminToolsPaneProps {
  firearm: Firearm
  onFirearmDeleted?: () => void
}

export default observer(function AdminToolsPane ({ firearm, onFirearmDeleted }: AdminToolsPaneProps) {
  const { firearmStore, userStore } = useStore()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [hideModalOpen, setHideModalOpen] = useState(false)
  const [flagModalOpen, setFlagModalOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!userStore.isAdmin) {
    return null
  }

  const handleDeleteFirearm = async () => {
    if (!firearm?.id) return
    setSubmitting(true)
    try {
      await firearmStore.deleteFirearm(firearm.id)
      toast.success('Firearm deleted successfully')
      setDeleteModalOpen(false)
      onFirearmDeleted?.()
    } catch (error) {
      toast.error('Failed to delete firearm')
    } finally {
      setSubmitting(false)
    }
  }

  const handleHideFirearm = async () => {
    if (!firearm?.id || !reason.trim()) {
      toast.error('Reason is required')
      return
    }
    setSubmitting(true)
    try {
      toast.info('Hide functionality not yet implemented')
      setHideModalOpen(false)
      setReason('')
    } finally {
      setSubmitting(false)
    }
  }

  const handleFlagFirearm = async () => {
    if (!firearm?.id || !reason.trim()) {
      toast.error('Reason is required')
      return
    }
    setSubmitting(true)
    try {
      toast.info('Flag functionality not yet implemented')
      setFlagModalOpen(false)
      setReason('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        padding: 16,
        marginTop: 24
      }}
    >
      <Group position="apart" mb="md">
        <Text weight={600} size="sm" sx={{ color: 'var(--gs-text)', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
          Admin Tools
        </Text>
        <Badge color="red" size="sm" variant="light">
          Moderator
        </Badge>
      </Group>

      <Stack spacing="sm">
        <Button
          variant="subtle"
          color="orange"
          leftIcon={<FiAlertCircle size={16} />}
          onClick={() => setHideModalOpen(true)}
          size="sm"
          fullWidth
          sx={{ justifyContent: 'flex-start', fontFamily: 'DM Sans, sans-serif' }}
        >
          Hide Firearm
        </Button>

        <Button
          variant="subtle"
          color="yellow"
          leftIcon={<FiFlag size={16} />}
          onClick={() => setFlagModalOpen(true)}
          size="sm"
          fullWidth
          sx={{ justifyContent: 'flex-start', fontFamily: 'DM Sans, sans-serif' }}
        >
          Flag for Review
        </Button>

        <Button
          variant="subtle"
          color="red"
          leftIcon={<FiTrash2 size={16} />}
          onClick={() => setDeleteModalOpen(true)}
          size="sm"
          fullWidth
          sx={{ justifyContent: 'flex-start', fontFamily: 'DM Sans, sans-serif' }}
        >
          Delete Firearm
        </Button>
      </Stack>

      <Modal
        opened={deleteModalOpen}
        title="Delete Firearm"
        onClose={() => setDeleteModalOpen(false)}
        centered
      >
        <Stack spacing="md">
          <Text size="sm" color="dimmed">
            Are you sure you want to permanently delete this firearm entry? This action cannot be undone.
          </Text>
          <Group position="right">
            <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={submitting}
              onClick={handleDeleteFirearm}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={hideModalOpen}
        title="Hide Firearm"
        onClose={() => {
          setHideModalOpen(false)
          setReason('')
        }}
        centered
      >
        <Stack spacing="md">
          <Text size="sm" color="dimmed">
            Hide this firearm from public view. Provide a reason for the action.
          </Text>
          <Textarea
            label="Reason"
            placeholder="Why is this firearm being hidden?"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group position="right">
            <Button variant="default" onClick={() => {
              setHideModalOpen(false)
              setReason('')
            }}>
              Cancel
            </Button>
            <Button
              color="orange"
              loading={submitting}
              onClick={handleHideFirearm}
            >
              Hide
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={flagModalOpen}
        title="Flag Firearm for Review"
        onClose={() => {
          setFlagModalOpen(false)
          setReason('')
        }}
        centered
      >
        <Stack spacing="md">
          <Text size="sm" color="dimmed">
            Flag this firearm for moderator review. Provide details about why it needs review.
          </Text>
          <Textarea
            label="Reason"
            placeholder="Why should this firearm be flagged for review?"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group position="right">
            <Button variant="default" onClick={() => {
              setFlagModalOpen(false)
              setReason('')
            }}>
              Cancel
            </Button>
            <Button
              color="yellow"
              loading={submitting}
              onClick={handleFlagFirearm}
            >
              Flag
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
})
